import { createSlice } from '@reduxjs/toolkit';
import { defineState } from 'redux-localstore';

const initialState = defineState([])('players');

export const playersSlice = createSlice({
  name: 'players',
  initialState,
  reducers: {
    createNewPlayer: (state, action) => {
      const {
        playerId, playerName, totalPoints, gender,
      } = action.payload;
      state.push({
        playerId,
        playerName,
        wins: 0,
        losses: 0,
        teamId: 0,
        isPlaying: false,
        waitingFor: 0,
        checkedIn: true,
        playerType: 'Flex', // flex, setter, attacker, both
        gender: gender || 'M', // M or F
        dailyWins: 0,
        dailyGames: 0,
        totalPoints: totalPoints || 0,
        stayIn: false,
        checkedInNow: true, // jogador recém-adicionado entra como "Check-in agora"
      });
    },
    setPlayerTeam: (state, action) => {
      const { playerId, teamId } = action.payload;
      const player = state.find((p) => p.playerId === playerId);
      player.teamId = teamId;
      player.checkedInNow = false; // ao entrar num time deixa de ser "check-in agora"
      if (teamId !== 4) {
        player.isPlaying = true;
      }
    },
    addWaitingFor: (state, action) => {
      const playerId = action.payload;
      const player = state.find((p) => p.playerId === playerId);
      player.waitingFor += 1;
    },
    // Incrementa a espera de todos que estão na fila (chamado ao iniciar cada partida).
    // Inclui os negativos (check-in na próxima): -0.01 -> 0.99 -> 1.99... (meio passo atrás).
    // Ao iniciar, "Saiu agora" e "Check-in agora" viram 1ª partida aguardando.
    incrementQueueWaiting: (state) => {
      state.forEach((player) => {
        if (player.teamId === 0 && player.checkedIn) {
          player.waitingFor += 1;
          player.checkedInNow = false;
        }
      });
    },
    addPriority: (state, action) => {
      const playerId = action.payload;
      const player = state.find((p) => p.playerId === playerId);
      player.waitingFor += 10;
    },
    removePriority: (state, action) => {
      const playerId = action.payload;
      const player = state.find((p) => p.playerId === playerId);
      if (player.waitingFor >= 10) {
        player.waitingFor -= 10;
      }
    },
    playerReady: (state, action) => {
      const playerId = action.payload;
      const player = state.find((p) => p.playerId === playerId);
      player.isPlaying = true;
    },
    removePlayer: (state, action) => {
      const playerId = action.payload;
      const player = state.find((p) => p.playerId === playerId);
      player.isPlaying = false;
      player.waitingFor = 0;
      player.teamId = 0;
    },
    endGame: (state, action) => state.map((player) => {
      if (player.teamId === 3) return player;
      const wasPlaying = player.teamId === 1 || player.teamId === 2;
      const won = action.payload.teamId === player.teamId;
      const currentTotalPoints = player.totalPoints ?? (player.wins - player.losses);
      let newTotalPoints = currentTotalPoints;
      if (won) {
        newTotalPoints = Math.min(100, currentTotalPoints + 1);
      } else if (wasPlaying) {
        newTotalPoints = Math.max(0, currentTotalPoints - 1);
      }
      return {
        ...player,
        waitingFor: player.isPlaying ? 0 : player.waitingFor + 1,
        isPlaying: won,
        wins: won ? player.wins + 1 : player.wins,
        losses: !won && wasPlaying ? player.losses + 1 : player.losses,
        teamId: won ? player.teamId : 0,
        dailyWins: won ? (player.dailyWins || 0) + 1 : (player.dailyWins || 0),
        dailyGames: wasPlaying ? (player.dailyGames || 0) + 1 : (player.dailyGames || 0),
        totalPoints: newTotalPoints,
      };
    }),
    // Encerra o jogo de forma parametrizada, cobrindo os 4 modos de encerramento.
    // payload: { winnerTeamId, loserTeamId, winnerStays, loserStays, saveWinner }
    resolveGame: (state, action) => {
      const {
        winnerTeamId, loserTeamId, winnerStays, loserStays, saveWinner,
      } = action.payload;
      return state.map((player) => {
        if (player.teamId === 3) return player; // não mexe no time salvo (TeamC)
        const isWinner = player.teamId === winnerTeamId;
        const isLoser = player.teamId === loserTeamId;
        const wasPlaying = isWinner || isLoser;

        let newTeamId = player.teamId;
        let newIsPlaying = player.isPlaying;
        if (isWinner) {
          const winnerStaysInCourt = !saveWinner && winnerStays;
          if (saveWinner) newTeamId = 3;
          else newTeamId = winnerStaysInCourt ? winnerTeamId : 0;
          newIsPlaying = winnerStaysInCourt;
        } else if (isLoser) {
          newTeamId = loserStays ? loserTeamId : 0;
          newIsPlaying = loserStays;
        }

        const currentTotalPoints = player.totalPoints ?? (player.wins - player.losses);
        let newTotalPoints = currentTotalPoints;
        if (isWinner) newTotalPoints = Math.min(100, currentTotalPoints + 1);
        else if (isLoser) newTotalPoints = Math.max(0, currentTotalPoints - 1);

        return {
          ...player,
          // Quem saiu vai para 0 ("Saiu agora"); quem já esperava não incrementa aqui.
          // O incremento agora acontece no início da próxima partida (incrementQueueWaiting).
          waitingFor: wasPlaying ? 0 : player.waitingFor,
          isPlaying: newIsPlaying,
          teamId: newTeamId,
          wins: isWinner ? player.wins + 1 : player.wins,
          losses: isLoser ? player.losses + 1 : player.losses,
          dailyWins: isWinner ? (player.dailyWins || 0) + 1 : (player.dailyWins || 0),
          dailyGames: wasPlaying ? (player.dailyGames || 0) + 1 : (player.dailyGames || 0),
          totalPoints: newTotalPoints,
        };
      });
    },
    checkIn: (state, action) => {
      const playerId = action.payload;
      const player = state.find((p) => p.playerId === playerId);
      player.checkedIn = true;
      player.teamId = 0; // entra na fila (garante que o incremento de espera o alcance)
      player.isPlaying = false;
      player.waitingFor = 0;
      player.checkedInNow = true; // "Check-in agora" até a próxima partida iniciar
    },
    checkInWithPriority: (state, action) => {
      const playerId = action.payload;
      const player = state.find((p) => p.playerId === playerId);
      player.checkedIn = true;
      player.teamId = 0; // entra na fila (garante que o incremento de espera o alcance)
      player.isPlaying = false;
      // Encontrar o menor waitingFor negativo atual
      const existingNegativeWaitings = state
        .filter((p) => p.checkedIn && p.waitingFor < 0)
        .map((p) => p.waitingFor)
        .sort((a, b) => a - b);

      let newWaitingFor = -0.01;
      if (existingNegativeWaitings.length > 0) {
        const mostNegative = existingNegativeWaitings[0];
        newWaitingFor = mostNegative - 0.01;
      }

      player.waitingFor = newWaitingFor;
    },
    checkOut: (state, action) => {
      const playerId = action.payload;
      const player = state.find((p) => p.playerId === playerId);
      player.checkedIn = false;
      player.teamId = 0; // sai de qualquer time ao fazer checkout
      player.isPlaying = false;
      player.waitingFor = 0;
      player.dailyWins = 0;
      player.dailyGames = 0;
    },
    checkOutAllPlayers: (state) => state.forEach((player) => {
      player.checkedIn = false;
      player.waitingFor = 0;
      player.dailyWins = 0;
      player.dailyGames = 0;
      player.teamId = 0;
      player.isPlaying = false;
      player.stayIn = false;
    }),
    removeAllPlayersFromTeamBoard: (state) => {
      state.forEach((player) => {
        player.teamId = 0;
        player.isPlaying = false;
      });
    },
    swapTeamIdsForAllPlayers: (state) => {
      state.forEach((player) => {
        if (player.teamId === 1) {
          player.teamId = 2;
        } else if (player.teamId === 2) {
          player.teamId = 1;
        }
      });
    },
    swapTeamIdToId3: (state, action) => {
      const { teamId } = action.payload;
      state.forEach((player) => {
        if (player.teamId === teamId) {
          player.teamId = 3;
          player.isPlaying = false;
        }
      });
    },
    swapTeamId3ToTeamId: (state, action) => {
      const { teamId } = action.payload;
      state.forEach((player) => {
        if (player.teamId === 3) {
          player.teamId = teamId;
          player.isPlaying = true;
        }
      });
    },
    swapTeamIdBetween2Players: (state, action) => {
      const { playerId1, playerId2 } = action.payload;
      const player1 = state.find((p) => p.playerId === playerId1);
      const player2 = state.find((p) => p.playerId === playerId2);
      const tempTeamId = player1.teamId;
      player1.teamId = player2.teamId;
      player2.teamId = tempTeamId;
    },
    deletePlayer: (state, action) => {
      const playerId = action.payload;
      return state.filter((p) => p.playerId !== playerId);
    },
    addWin: (state, action) => {
      const playerId = action.payload;
      const player = state.find((p) => p.playerId === playerId);
      player.wins += 1;
    },
    addLoss: (state, action) => {
      const playerId = action.payload;
      const player = state.find((p) => p.playerId === playerId);
      player.losses += 1;
    },
    addTotalPoints: (state, action) => {
      const playerId = action.payload;
      const player = state.find((p) => p.playerId === playerId);
      const currentPoints = player.totalPoints ?? (player.wins - player.losses);
      player.totalPoints = Math.min(100, currentPoints + 1);
    },
    removeTotalPoints: (state, action) => {
      const playerId = action.payload;
      const player = state.find((p) => p.playerId === playerId);
      const currentPoints = player.totalPoints ?? (player.wins - player.losses);
      player.totalPoints = Math.max(0, currentPoints - 1);
    },
    setTotalPoints: (state, action) => {
      const { playerId, totalPoints } = action.payload;
      const player = state.find((p) => p.playerId === playerId);
      player.totalPoints = Math.min(100, Math.max(0, Math.round(totalPoints) || 0));
    },
    changePlayerType: (state, action) => {
      const { playerId, playerType } = action.payload;
      const player = state.find((p) => p.playerId === playerId);
      player.playerType = playerType;
    },
    updatePlayerName: (state, action) => {
      const { playerId, playerName } = action.payload;
      const player = state.find((p) => p.playerId === playerId);
      player.playerName = playerName;
    },
    toggleStayIn: (state, action) => {
      const playerId = action.payload;
      const player = state.find((p) => p.playerId === playerId);
      player.stayIn = !player.stayIn;
    },
    resetAllStayIn: (state) => {
      state.forEach((player) => {
        player.stayIn = false;
      });
    },
  },
});

export const {
  addWaitingFor,
  incrementQueueWaiting,
  createNewPlayer,
  setPlayerTeam,
  playerReady,
  removePlayer,
  endGame,
  resolveGame,
  checkIn,
  checkInWithPriority,
  checkOut,
  checkOutAllPlayers,
  removeAllPlayersFromTeamBoard,
  swapTeamIdsForAllPlayers,
  swapTeamIdBetween2Players,
  deletePlayer,
  addWin,
  addLoss,
  addTotalPoints,
  removeTotalPoints,
  setTotalPoints,
  changePlayerType,
  swapTeamIdToId3,
  swapTeamId3ToTeamId,
  addPriority,
  removePriority,
  updatePlayerName,
  toggleStayIn,
  resetAllStayIn,
} = playersSlice.actions;
